/**
 * UI / Branding management panel.
 *
 * Today: read-only preview of the active theme. The theme itself is set at
 * build time via `REACT_APP_BRAND_*` env vars (see src/theme/index.ts).
 *
 * Next step (when a customer asks): persist overrides in the DB and have
 * the frontend fetch them at mount, so admins can rebrand without rebuilding.
 */
import React from 'react';
import { Card, Typography, Alert, Tag, Space, Descriptions } from 'antd';
import { brand, colors } from '../../theme';

const { Title, Text, Paragraph } = Typography;

const Swatch: React.FC<{ value: string }> = ({ value }) => (
  <Space size={8}>
    <div style={{
      width: 20, height: 20, borderRadius: 4,
      background: value, border: '1px solid #e8e8e8',
    }} />
    <Text code>{value}</Text>
  </Space>
);

const UIManagementPanel: React.FC = () => {
  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>Gestionar UI</Title>
      <Paragraph type="secondary">
        Configura la marca de esta instalación: nombre del producto, logo y
        colores. Lo que se muestra aquí es el theme activo en este build.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        message="Edición en runtime aún no disponible"
        description={
          <>
            Por ahora el theme se fija al compilar via variables{' '}
            <Text code>REACT_APP_BRAND_*</Text>. Próxima iteración: permitir editar
            estos valores aquí y persistirlos en la base de datos para que cada
            cliente tenga su marca sin recompilar.
          </>
        }
      />

      <Card title="Identidad de marca" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small" labelStyle={{ width: 200, color: '#8c8c8c' }}>
          <Descriptions.Item label="Nombre del producto">
            <Text strong>{brand.name}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">{brand.vendor}</Descriptions.Item>
          <Descriptions.Item label="Tagline">{brand.tagline}</Descriptions.Item>
          <Descriptions.Item label="Logo URL">
            {brand.logoUrl ? (
              <Space>
                <img src={brand.logoUrl} alt={brand.name} style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'contain' }} />
                <Text code>{brand.logoUrl}</Text>
              </Space>
            ) : (
              <Tag>Sin URL — se usa el gradiente como fallback</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Gradient">
            <Space size={8}>
              <div style={{
                width: 60, height: 24, borderRadius: 4,
                background: `linear-gradient(135deg, ${brand.gradientFrom} 0%, ${brand.gradientTo} 100%)`,
              }} />
              <Text code>{brand.gradientFrom}</Text>
              <Text type="secondary">→</Text>
              <Text code>{brand.gradientTo}</Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="Soporte">
            {brand.supportEmail || brand.website
              ? <span>{brand.supportEmail} {brand.website && <Text type="secondary">· {brand.website}</Text>}</span>
              : <Tag>Sin configurar</Tag>}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Colores" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={1} size="small" labelStyle={{ width: 200, color: '#8c8c8c' }}>
          <Descriptions.Item label="Primary">
            <Swatch value={colors.primary} />
          </Descriptions.Item>
          <Descriptions.Item label="Success">
            <Swatch value={colors.success} />
          </Descriptions.Item>
          <Descriptions.Item label="Warning">
            <Swatch value={colors.warning} />
          </Descriptions.Item>
          <Descriptions.Item label="Error">
            <Swatch value={colors.error} />
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title="Cómo cambiar el theme (build time)" size="small">
        <Paragraph>
          Crea un <Text code>.env.production</Text> en{' '}
          <Text code>builder/frontend/</Text> con las variables que quieras
          override, luego rebuilda con <Text code>npm run build</Text>.
        </Paragraph>
        <Paragraph>
          Ejemplo para un cliente <Text strong>ACME</Text>:
        </Paragraph>
        <pre style={{
          background: '#f6f8fa', padding: 12, borderRadius: 6,
          fontSize: 12, lineHeight: 1.6, overflow: 'auto',
        }}>{`REACT_APP_BRAND_NAME=ACME Studio
REACT_APP_BRAND_VENDOR=ACME Corp
REACT_APP_BRAND_TAGLINE=Agent Builder
REACT_APP_BRAND_LOGO_URL=https://cdn.acme.com/logo.svg
REACT_APP_BRAND_GRADIENT_FROM=#dc2626
REACT_APP_BRAND_GRADIENT_TO=#f97316
REACT_APP_PRIMARY_COLOR=#dc2626`}</pre>
      </Card>
    </div>
  );
};

export default UIManagementPanel;
